import { useEffect, useState } from "react";
import axios from "axios";
import { Star } from "lucide-react";
import { toast } from "sonner";
import { useApp } from "../context/AppContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ProductReviews({ slug }) {
  const [reviews, setReviews] = useState([]);
  const [avg, setAvg] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [author, setAuthor] = useState("");
  const [email, setEmail] = useState("");
  const [rating, setRating] = useState(5);
  const [text, setText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const { user } = useApp() || {};

  useEffect(() => {
    if (user?.email) setEmail(user.email);
    if (user?.name) setAuthor(user.name);
  }, [user]);

  useEffect(() => {
    if (!slug) return;
    axios.get(`${API}/products/${slug}/reviews`).then((r) => {
      setReviews(r.data?.items || []);
      setAvg(r.data?.average ?? null);
    }).catch(() => {});
  }, [slug]);

  async function submit(e) {
    e.preventDefault();
    if (text.trim().length < 10) {
      toast.error("Votre avis doit faire au moins 10 caractères");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/products/reviews`, {
        product_slug: slug, author, email: email || null, rating, text,
      });
      toast.success("Merci pour votre avis ! Il sera publié après modération.");
      setShowForm(false);
      setAuthor(""); setText(""); setRating(5);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur — réessayez");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <section className="py-12 border-t border-ink/10" data-testid="product-reviews">
      <div className="max-w-3xl mx-auto px-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="font-display text-2xl md:text-3xl">Avis clients</h2>
            {avg ? (
              <div className="flex items-center gap-2 mt-2">
                <div className="flex">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <Star key={n} size={16} className={n <= Math.round(avg) ? "fill-coral text-coral" : "text-ink/20"} />
                  ))}
                </div>
                <span className="text-sm text-ink-soft">{avg.toFixed(1)} / 5 · {reviews.length} avis</span>
              </div>
            ) : (
              <p className="text-sm text-ink-soft mt-1">Soyez le premier à laisser un avis</p>
            )}
          </div>
          <button
            type="button"
            onClick={() => setShowForm((s) => !s)}
            data-testid="open-review-form-btn"
            className="px-4 py-2 rounded-full border border-ink/20 hover:bg-ivory-200 text-sm"
          >
            {showForm ? "Annuler" : "Donner mon avis"}
          </button>
        </div>

        {showForm && (
          <form onSubmit={submit} className="bg-ivory-100 rounded-2xl p-6 mb-8 space-y-4" data-testid="review-form">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="text-xs uppercase tracking-wider text-ink/60">Votre prénom</label>
                <input
                  type="text"
                  value={author}
                  onChange={(e) => setAuthor(e.target.value)}
                  required
                  minLength={2}
                  maxLength={80}
                  data-testid="review-author"
                  className="w-full mt-1 px-3 py-2 rounded-xl border border-ink/15 bg-white"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-ink/60">Email (optionnel, pour vérifier)</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="review-email"
                  className="w-full mt-1 px-3 py-2 rounded-xl border border-ink/15 bg-white"
                />
              </div>
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-ink/60">Note</label>
              <div className="flex gap-1 mt-1" data-testid="review-stars">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button
                    type="button"
                    key={n}
                    onClick={() => setRating(n)}
                    aria-label={`${n} étoile${n > 1 ? "s" : ""}`}
                    data-testid={`review-star-${n}`}
                    className="p-1"
                  >
                    <Star size={24} className={n <= rating ? "fill-coral text-coral" : "text-ink/20"} />
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-ink/60">Votre avis</label>
              <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                required
                minLength={10}
                maxLength={2000}
                rows={4}
                data-testid="review-text"
                placeholder="Partagez votre expérience…"
                className="w-full mt-1 px-3 py-2 rounded-xl border border-ink/15 bg-white"
              />
            </div>
            <div className="flex items-center justify-end gap-2">
              <button type="submit" disabled={submitting} data-testid="submit-review-btn"
                className="px-5 py-2 rounded-full bg-ink text-white text-sm disabled:opacity-60">
                {submitting ? "Envoi…" : "Publier mon avis"}
              </button>
            </div>
          </form>
        )}

        <div className="space-y-6">
          {reviews.length === 0 && !showForm && (
            <p className="text-sm text-ink-soft" data-testid="no-reviews-yet">Pas encore d&apos;avis pour ce produit.</p>
          )}
          {reviews.map((r) => (
            <article key={r.id} className="border-b border-ink/10 pb-6" data-testid={`review-${r.id}`}>
              <div className="flex items-center justify-between mb-2">
                <div>
                  <p className="font-medium text-sm">
                    {r.author}
                    {r.verified_buyer && (
                      <span className="ms-2 inline-block text-[10px] px-2 py-0.5 rounded-full bg-pedablue/10 text-pedablue uppercase tracking-wider">
                        Achat vérifié
                      </span>
                    )}
                  </p>
                  <p className="text-xs text-ink-soft">{new Date(r.created_at).toLocaleDateString("fr-FR")}</p>
                </div>
                <div className="flex">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <Star key={n} size={14} className={n <= r.rating ? "fill-coral text-coral" : "text-ink/20"} />
                  ))}
                </div>
              </div>
              <p className="text-sm leading-relaxed">{r.text}</p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
